import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import { BlockSkeleton } from "../interfaccia/loading";
import {
  MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS,
  MATCH_BOTTERO_MOTIVI_LABELS,
  MATCH_BOTTERO_READ_ONLY_ADVANCED_FILTER_FIELDS,
  MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS,
} from "./MatchBotteroReadOnlyShared";

function MatchBotteroReasonSummarySkeleton() {
  return (
    <div className="flex flex-wrap">
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="w-100 w-third-l pa3" key={`reason-skeleton-${index}`}>
          <BlockSkeleton width={120} height={18} className="mb3" />
          <div className="overflow-auto">
            <table className="collapse w-100">
              <tbody>
                {Array.from({ length: 4 }).map((__, rowIndex) => (
                  <tr key={`reason-row-${index}-${rowIndex}`}>
                    <td className="pa1 bb bordoColAlt">
                      <BlockSkeleton
                        width={rowIndex === 3 ? "68%" : "82%"}
                        height={14}
                      />
                    </td>
                    <td className="pa1 bb bordoColAlt tr">
                      <div className="flex justify-end">
                        <BlockSkeleton width={52} height={14} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function MatchBotteroStatsPage({ token }) {
  const [kpi, setKpi] = useState(null);
  const [loadingKpi, setLoadingKpi] = useState(false);
  const [kpiError, setKpiError] = useState("");
  const [filters, setFilters] = useState(
    MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS,
  );
  const [draftFilters, setDraftFilters] = useState(
    MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS,
  );
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const loadKpi = useCallback(async () => {
    if (!token) return;
    setLoadingKpi(true);
    setKpiError("");
    try {
      const params = {
        includeOutOfMovimenti: 1,
        includeReasonSummary: 1,
      };
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim().length) {
          params[key] = value.trim();
        }
      });
      const response = await axios.get(`${apiUrl}/moda/match-bottero/kpi`, {
        headers: authHeaders,
        params,
      });
      setKpi(response?.data?.kpi ?? null);
    } catch (err) {
      setKpi(null);
      setKpiError(
        err?.response?.data?.error ??
          err?.message ??
          "Errore caricamento recap motivi",
      );
    } finally {
      setLoadingKpi(false);
    }
  }, [authHeaders, filters, token]);

  useEffect(() => {
    loadKpi();
  }, [loadKpi]);

  const updateDraftFilter = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(draftFilters);
  };

  const resetFilters = () => {
    setDraftFilters(MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS);
    setFilters(MATCH_BOTTERO_READ_ONLY_DEFAULT_FILTERS);
  };

  const reasonSummary = kpi?.reasonSummary || {};
  const reasonArtVar = Array.isArray(reasonSummary.artVar)
    ? reasonSummary.artVar
    : [];
  const reasonTaglia = Array.isArray(reasonSummary.taglia)
    ? reasonSummary.taglia
    : [];
  const reasonCreazione = Array.isArray(reasonSummary.creazione)
    ? reasonSummary.creazione
    : [];
  const reasonIgnore = Array.isArray(reasonSummary.ignore)
    ? reasonSummary.ignore
    : [];

  const renderReasonSummary = (title, rowsSummary) => (
    <div className="w-100 w-third-l pa3" key={title}>
      <div className="b mb2">{title}</div>
      {rowsSummary.length ? (
        <div className="overflow-auto">
          <table className="collapse w-100">
            <tbody>
              {rowsSummary.map((row) => (
                <tr key={row.code}>
                  <td className="pa1 bb bordoColAlt">
                    {row.label || row.code}
                  </td>
                  <td className="pa1 bb bordoColAlt tr b">
                    {Number(row.count || 0).toLocaleString("it-IT")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mid-gray">Nessun motivo rilevato.</div>
      )}
    </div>
  );

  return (
    <div className="rootTema pa2 pa3-ns contentSubHeader">
      <div className="sfondoBiancoTema br3 pa3">
        <div className="pa3 sfondoColAltScuro mb3 flex justify-between items-center">
          <div className="f3 b">Stats match</div>
          <form
            className="flex flex-wrap items-end"
            onSubmit={(e) => {
              e.preventDefault();
              applyFilters();
            }}
          >
            <div className="mr3 mb2">
              <label className="f7 db mb1">BS Articolo</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.bsArticolo}
                onChange={(e) =>
                  updateDraftFilter("bsArticolo", e.target.value)
                }
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">VS Articolo</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.vsArticolo}
                onChange={(e) =>
                  updateDraftFilter("vsArticolo", e.target.value)
                }
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">BS Nome</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.bsNome}
                onChange={(e) => updateDraftFilter("bsNome", e.target.value)}
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">VS Nome</label>
              <input
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.vsNome}
                onChange={(e) => updateDraftFilter("vsNome", e.target.value)}
              />
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">OK</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.ok}
                onChange={(e) => updateDraftFilter("ok", e.target.value)}
              >
                <option value="">Tutti</option>
                <option value="1">Solo OK</option>
                <option value="0">Solo non OK</option>
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Giacenza BS</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.hasGiacenza}
                onChange={(e) =>
                  updateDraftFilter("hasGiacenza", e.target.value)
                }
              >
                <option value="">Tutte</option>
                <option value="1">Solo giacenza &gt; 0</option>
                <option value="0">Solo giacenza = 0</option>
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo art-var</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviArtVar}
                onChange={(e) =>
                  updateDraftFilter("motiviArtVar", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS.motiviArtVar.map(
                  (code) => (
                    <option key={code} value={code}>
                      {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                    </option>
                  ),
                )}
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo taglia</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviTaglia}
                onChange={(e) =>
                  updateDraftFilter("motiviTaglia", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS.motiviTaglia.map(
                  (code) => (
                    <option key={code} value={code}>
                      {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                    </option>
                  ),
                )}
              </select>
            </div>
            <div className="mr3 mb2">
              <label className="f7 db mb1">Motivo creazione</label>
              <select
                className="input-reset ba bordoColAlt br2 pa2"
                value={draftFilters.motiviCreazione}
                onChange={(e) =>
                  updateDraftFilter("motiviCreazione", e.target.value)
                }
              >
                <option value="">Tutti</option>
                {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS.motiviCreazione.map(
                  (code) => (
                    <option key={code} value={code}>
                      {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                    </option>
                  ),
                )}
              </select>
            </div>
            <button
              type="submit"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 mr3"
            >
              Cerca
            </button>
            <button
              type="button"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2 mr3"
              onClick={() => setShowAdvancedFilters(true)}
            >
              Ricerca avanzata
            </button>
            <button
              type="button"
              className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
              onClick={resetFilters}
            >
              Reset filtri
            </button>
          </form>
        </div>

        <div className="pa3 pt0">
          <div className="bl bordoColAlt br2 ph2 pt2 pb2 sfondoGrigioChiaroTema">
            <div className="b mb1">
              Recap motivi (match/creazioni coinvolti)
            </div>
            {loadingKpi ? (
              <MatchBotteroReasonSummarySkeleton />
            ) : kpiError ? (
              <div className="dark-red">{kpiError}</div>
            ) : (
              <div className="flex flex-wrap">
                {renderReasonSummary("Art-var", reasonArtVar)}
                {renderReasonSummary("Taglia", reasonTaglia)}
                {renderReasonSummary("Creazione", reasonCreazione)}
                {renderReasonSummary("Ignorato", reasonIgnore)}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAdvancedFilters ? (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-5"
          style={{ background: "rgba(0, 0, 0, 0.35)" }}
          onClick={() => setShowAdvancedFilters(false)}
        >
          <div
            className="sfondoBiancoTema br3 ba bordoColAlt"
            style={{
              width: "min(1100px, 96vw)",
              maxHeight: "88vh",
              margin: "5vh auto",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pa3">
              <div className="flex items-center justify-between mb3">
                <h3 className="ma0">Ricerca avanzata Stats match</h3>
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco"
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  Chiudi
                </button>
              </div>
              <div className="flex flex-wrap">
                {MATCH_BOTTERO_READ_ONLY_ADVANCED_FILTER_FIELDS.map(
                  ([key, label]) => (
                    <div key={key} className="w-100 w-50-m w-third-l pr3 mb3">
                      <label className="db mb1">{label}</label>
                      {Object.prototype.hasOwnProperty.call(
                        MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS,
                        key,
                      ) ? (
                        <select
                          className="input-reset ba bordoColAlt br2 pa2 w-100"
                          value={draftFilters[key] || ""}
                          onChange={(e) =>
                            updateDraftFilter(key, e.target.value)
                          }
                        >
                          <option value="">Tutti</option>
                          {MATCH_BOTTERO_MOTIVI_FILTER_OPTIONS[key].map(
                            (code) => (
                              <option key={code} value={code}>
                                {MATCH_BOTTERO_MOTIVI_LABELS[code] || code}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <input
                          className="input-reset ba bordoColAlt br2 pa2 w-100"
                          value={draftFilters[key] || ""}
                          onChange={(e) =>
                            updateDraftFilter(key, e.target.value)
                          }
                        />
                      )}
                    </div>
                  ),
                )}
                <div className="w-100 w-50-m w-third-l pr3 mb3">
                  <label className="db mb1">Giacenza BS</label>
                  <select
                    className="input-reset ba bordoColAlt br2 pa2 w-100"
                    value={draftFilters.hasGiacenza}
                    onChange={(e) =>
                      updateDraftFilter("hasGiacenza", e.target.value)
                    }
                  >
                    <option value="">Tutte</option>
                    <option value="1">Solo giacenza &gt; 0</option>
                    <option value="0">Solo giacenza = 0</option>
                  </select>
                </div>
              </div>
              <div className="mt2 flex flex-wrap items-center">
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mr2 mb2"
                  onClick={() => {
                    applyFilters();
                    setShowAdvancedFilters(false);
                  }}
                >
                  Applica filtri
                </button>
                <button
                  type="button"
                  className="pointer bn br2 ph3 pv2 sfondoColScuro colBianco mb2"
                  onClick={() => {
                    resetFilters();
                    setShowAdvancedFilters(false);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MatchBotteroStatsPage;
